import React, { useMemo, useRef, useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';

function fixMojibake(value) {
  const text = String(value || '');
  try {
    return decodeURIComponent(escape(text));
  } catch {
    return text;
  }
}

function localizeChartLabel(label, isEnglishUi) {
  const source = fixMojibake(label);
  if (!isEnglishUi) return source;

  const key = source
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[¿?¡!.,:;()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const map = {
    'ventas netas': 'Net sales',
    'ventas brutas': 'Gross sales',
    devoluciones: 'Returns',
    clientes: 'Customers',
    'clientes activos': 'Active customers',
    'nuevos clientes': 'New customers',
    'tasa recurrencia': 'Repeat rate',
    'ticket promedio': 'Average order value'
  };

  if (map[key]) return map[key];

  return source
    .replace(/ventas netas/gi, 'Net sales')
    .replace(/ventas brutas/gi, 'Gross sales')
    .replace(/devoluciones/gi, 'Returns')
    .replace(/clientes activos/gi, 'Active customers')
    .replace(/nuevos clientes/gi, 'New customers')
    .replace(/tasa recurrencia/gi, 'Repeat rate')
    .replace(/ticket promedio/gi, 'Average order value');
}

function sanitizeChartTitle(rawTitle) {
  const title = fixMojibake(rawTitle || 'Gráfica');
  return title
    .replace(/\b(?:ia|ai)\b\s*[:-]?\s*/gi, '')
    .replace(/[^\p{L}\p{N}\p{M}\p{P}\p{Zs}%$]/gu, '')
    .replace(/\s{2,}/g, ' ')
    .trim() || 'Gráfica';
}

function normalizeToECharts(viz) {
  if (!viz || viz.type !== 'visualization') return null;
  
  // Soporte formato simple y tipo Chart.js
  if (Array.isArray(viz.data) && (viz.xKey || viz.yKey)) {
    const xKey = viz.xKey || 'x';
    const yKey = viz.yKey || 'y';
    return {
      title: sanitizeChartTitle(viz.title || 'Gráfica'),
      chartType: viz.chart || 'bar',
      xAxisType: 'category',
      xData: viz.data.map(d => d[xKey]),
      series: [{ name: viz.seriesName || yKey, type: viz.chart === 'bar' ? 'bar' : 'line', data: viz.data.map(d => d[yKey]) }]
    };
  }

  // ✅ NUEVO: Soporte para formato directo del Dashboard (viz.data = { labels, datasets })
  if (viz.data && Array.isArray(viz.data.labels) && Array.isArray(viz.data.datasets)) {
    const labels = viz.data.labels;
    const datasets = viz.data.datasets;
    const chartType = (viz.chart || 'bar').toLowerCase();
    
    // Para pie/doughnut, convertir a formato ECharts
    if (chartType === 'pie' || chartType === 'doughnut') {
      const ds = datasets[0] || { label: 'value', data: [] };
      return {
        title: sanitizeChartTitle(viz.title || 'Gráfica'),
        chartType: chartType,
        customColors: ds.backgroundColor || null,
        series: [{
          name: ds.label || 'value',
          type: 'pie',
          radius: chartType === 'doughnut' ? ['40%', '70%'] : '70%',
          data: labels.map((label, idx) => ({
            name: label,
            value: ds.data[idx] || 0
          }))
        }]
      };
    }
    
    // Para bar/line, mapear datasets
    return {
      title: sanitizeChartTitle(viz.title || 'Gráfica'),
      chartType: chartType,
      xAxisType: 'category',
      xData: labels,
      series: datasets.map(ds => ({
        name: ds.label || 'value',
        type: chartType === 'line' ? 'line' : 'bar',
        data: ds.data,
        backgroundColor: ds.backgroundColor || null,
        borderColor: ds.borderColor || null
      }))
    };
  }

  const details = viz.visualizationDetails || viz.grafico || viz.chartSpec || null;
  if (details && details.data && Array.isArray(details.data.labels) && Array.isArray(details.data.datasets)) {
    const labels = details.data.labels;
    const datasets = details.data.datasets;
    const chartType = (details.chartType || details.type || 'bar').toLowerCase();
    
    // Para pie/doughnut, convertir a formato ECharts
    if (chartType === 'pie' || chartType === 'doughnut') {
      const ds = datasets[0] || { label: 'value', data: [] };
      return {
        title: sanitizeChartTitle(details.title || viz.title || 'Gráfica'),
        chartType: chartType,
        customColors: ds.backgroundColor || null, // Guardar colores personalizados
        series: [{
          name: ds.label || 'value',
          type: 'pie',
          radius: chartType === 'doughnut' ? ['40%', '70%'] : '70%',
          data: labels.map((label, idx) => ({
            name: label,
            value: ds.data[idx] || 0
          }))
        }]
      };
    }
    
    // Para bar/line, mapear todos los datasets CON sus colores
    return {
      title: sanitizeChartTitle(details.title || viz.title || 'Gráfica'),
      chartType: chartType,
      xAxisType: 'category',
      xData: labels,
      series: datasets.map(ds => ({
        name: ds.label || 'value',
        type: chartType === 'line' ? 'line' : 'bar',
        data: ds.data,
        backgroundColor: ds.backgroundColor || null, // Colores del backend
        borderColor: ds.borderColor || null
      }))
    };
  }
  return null;
}

export default function EChartRenderer({ viz, hideTitle = false }) {
  const ref = useRef(null);
  const norm = useMemo(() => normalizeToECharts(viz), [viz]);
  const chartMeta = useMemo(() => {
    const xData = Array.isArray(norm?.xData) ? norm.xData : [];
    const categoryCount = xData.length;
    const avgLabelLength = categoryCount
      ? xData.reduce((sum, label) => sum + String(label || '').length, 0) / categoryCount
      : 0;
    const numSeries = Array.isArray(norm?.series) ? norm.series.length : 0;
    const hasMultipleSeries = numSeries >= 3;
    const shouldUseHorizontalBar =
      norm?.chartType === 'bar' &&
      numSeries === 1 &&
      (categoryCount >= 8 || avgLabelLength > 12);
    const denseVerticalBar =
      norm?.chartType === 'bar' &&
      !shouldUseHorizontalBar &&
      (categoryCount >= 10 || avgLabelLength > 10);

    return {
      categoryCount,
      avgLabelLength,
      numSeries,
      hasMultipleSeries,
      shouldUseHorizontalBar,
      denseVerticalBar
    };
  }, [norm]);

  // Estado para forzar re-render cuando cambia el tema
  const [themeKey, setThemeKey] = useState(0);
  
  // Detectar cambios en el tema
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setThemeKey(prev => prev + 1);
        }
      });
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  // Colores modernos y profesionales (Enterprise Palette)
  const colorPalette = useMemo(() => [
    ['#6366f1', '#818cf8'], // Indigo
    ['#10b981', '#34d399'], // Emerald
    ['#f59e0b', '#fbbf24'], // Amber
    ['#ef4444', '#f87171'], // Red
    ['#06b6d4', '#22d3ee'], // Cyan
    ['#8b5cf6', '#a78bfa'], // Violet
    ['#ec4899', '#f472b6'], // Pink
  ], []);
  
  // Función para convertir rgba a rgb (sin opacidad)
  const parseColor = (colorStr) => {
    if (!colorStr) return null;
    const rgbaMatch = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbaMatch) {
      return `rgb(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]})`;
    }
    return colorStr;
  };
  
  // Función para formatear números grandes de forma compacta (K, M, B)
  const formatCompactNumber = (value) => {
    if (value === null || value === undefined) return '0';
    
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    
    if (absValue >= 1e9) {
      return sign + (absValue / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
    }
    if (absValue >= 1e6) {
      return sign + (absValue / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (absValue >= 1e3) {
      return sign + (absValue / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return sign + absValue.toFixed(0);
  };

  // Función para añadir opacidad a un color RGB/RGBA
  const addOpacity = (colorStr, opacity) => {
    if (!colorStr) return `rgba(139, 92, 246, ${opacity})`;
    
    // Limpiar espacios
    const cleanColor = colorStr.trim();
    
    // Si ya es rgba, reemplazar la opacidad
    const rgbaMatch = cleanColor.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+\s*)?\)/);
    if (rgbaMatch) {
      return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${opacity})`;
    }
    
    // Si es hex, convertir a rgba
    if (cleanColor.startsWith('#')) {
      const hex = cleanColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    
    // Fallback: morado con opacidad
    console.warn('Color no reconocido:', colorStr);
    return `rgba(139, 92, 246, ${opacity})`;
  };

  const chartHeight = useMemo(() => {
    if (!hideTitle) return 600;
    if (!norm) return 360;
    if (norm.chartType === 'line') return 390;
    if (norm.chartType === 'bar' && chartMeta.shouldUseHorizontalBar) {
      return Math.min(620, Math.max(420, chartMeta.categoryCount * 34));
    }
    if (norm.chartType === 'bar') return 420;
    return 380;
  }, [hideTitle, norm, chartMeta]);
  
  const option = useMemo(() => {
    if (!norm) return null;

    const safeSeries = Array.isArray(norm.series) ? norm.series : [];
    const isPieChart = norm.chartType === 'pie' || norm.chartType === 'doughnut';
    const {
        categoryCount,
        numSeries,
        hasMultipleSeries,
        shouldUseHorizontalBar,
        denseVerticalBar
    } = chartMeta;
    const titleForFormatting = String(norm.title || '').toLowerCase();
    const isCurrencyMetric = /(venta|ventas|revenue|ingres|sales|gross|net|devoluc|refund|spend|payment)/i.test(titleForFormatting);
    const isPercentMetric = /(rate|ctr|ctor|porcent|%)/i.test(titleForFormatting);
    const isEnglishUi = (document.documentElement.lang || '').toLowerCase().startsWith('en');
    const formatMetricValue = (value) => {
      const numericValue = Number(value || 0);
      if (isPercentMetric) return `${numericValue.toFixed(1)}%`;
      if (isCurrencyMetric) return `$${formatCompactNumber(numericValue)}`;
      return formatCompactNumber(numericValue);
    };
    
    // Leer colores dinámicos del tema actual
    const textColor = getComputedStyle(document.documentElement).getPropertyValue('--text-primary').trim() || '#ECECEC';
    const borderColor = getComputedStyle(document.documentElement).getPropertyValue('--border-color').trim() || '#3a3a3a';
    const tooltipBg = getComputedStyle(document.documentElement).getPropertyValue('--bg-secondary').trim() || '#212121';
    const hasLegend = !isPieChart && numSeries > 1;
    
    return {
    backgroundColor: 'transparent',
    textStyle: { 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: textColor
    },
    // Habilitar animaciones suaves
    animation: true,
    animationDuration: 800,
    animationEasing: 'cubicOut',
    animationDelay: (idx) => idx * 50,  // Animación escalonada
    // Opciones de interacción
    emphasis: {
      focus: 'series',  // Al hacer hover en una serie, atenuar las demás
      blurScope: 'coordinateSystem'
    },
    title: hideTitle ? { show: false } : { 
      text: norm.title, 
      left: 'center',
      top: 30,
      textStyle: { 
        color: textColor,
        fontWeight: 700, 
        fontSize: 22
      } 
    },
    tooltip: isPieChart ? {
      trigger: 'item',
      backgroundColor: tooltipBg,
      borderColor: borderColor,
      borderWidth: 2,
      textStyle: { 
        color: textColor,
        fontSize: 14,
        fontWeight: 600
      },
      padding: [12, 16],
      formatter: '{b}: {c} ({d}%)'
    } : { 
      trigger: 'axis',
      backgroundColor: tooltipBg,
      borderColor: borderColor,
      borderWidth: 2,
      textStyle: { 
        color: textColor,
        fontSize: 14,
        fontWeight: 600
      },
      padding: [12, 16],
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: 'rgba(139, 92, 246, 0.15)'
        }
      },
      // Formatter personalizado para tooltips más informativos
      formatter: (params) => {
        if (!Array.isArray(params)) params = [params];
        
        let result = `<div style="font-weight: 700; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid ${borderColor}">${params[0].axisValue}</div>`;
        
        params.forEach(param => {
          const marker = param.marker;
          const seriesName = localizeChartLabel(param.seriesName, isEnglishUi);
          const value = param.value;
          const formattedValue = formatMetricValue(value);
          
          result += `<div style="margin-top: 6px; display: flex; justify-content: space-between; align-items: center;">
            <span>${marker} ${seriesName}:</span>
            <span style="margin-left: 20px; font-weight: 700;">${formattedValue}</span>
          </div>`;
        });
        
        return result;
      }
    },
    legend: isPieChart ? {
      orient: 'vertical',
      left: 'left',
      top: 'middle',
      textStyle: {
        color: textColor,
        fontSize: 13,
        fontWeight: 600
      },
      selected: undefined,  // Todas las series visibles por defecto
      selector: true,  // Habilitar selectores también para pie charts
      selectorLabel: {
        color: textColor,
        fontSize: 11
      },
      tooltip: {
        show: true  // Tooltips en leyenda de pie
      }
    } : (numSeries > 1 ? {
      // Legend interactiva para gráficos con múltiples series
      type: 'scroll',  // Permite scroll si hay muchas series
      orient: 'horizontal',
      left: 'center',
      top: hideTitle ? 8 : 40,
      textStyle: {
        color: textColor,
        fontSize: 13,
        fontWeight: 600
      },
      itemGap: 20,
      itemWidth: 25,
      itemHeight: 14,
      icon: 'roundRect',
      selected: undefined,  // Todas las series visibles por defecto
      selector: [
        {
          type: 'all',
          title: 'Todas'
        },
        {
          type: 'inverse',
          title: 'Invertir'
        }
      ],
      selectorLabel: {
        color: textColor,
        fontSize: 12,
        fontWeight: 600
      },
      pageIconColor: textColor,
      pageIconInactiveColor: borderColor,
      pageTextStyle: {
        color: textColor
      },
      tooltip: {
        show: true
      }
    } : undefined),
    grid: !isPieChart ? { 
      left: shouldUseHorizontalBar ? 170 : 56,
      right: 28,
      top: hasLegend ? (hideTitle ? 64 : 96) : (hideTitle ? 12 : 60),
      bottom: shouldUseHorizontalBar ? 18 : (denseVerticalBar ? 86 : 54),
      containLabel: true
    } : undefined,
    // Ajuste global para barras múltiples - espaciado uniforme
    ...(hasMultipleSeries && !isPieChart && norm.chartType === 'bar' ? {
      barCategoryGap: '25%',  // Espacio entre grupos de barras
      barGap: '5%'            // Espacio entre barras dentro de un grupo
    } : {}),
    xAxis: !isPieChart ? (
      shouldUseHorizontalBar ? {
        type: 'value',
        axisLabel: {
          color: textColor,
          fontSize: 12,
          fontWeight: 600,
          margin: 12,
          formatter: (value) => formatMetricValue(value)
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          lineStyle: {
            color: borderColor,
            type: 'dashed',
            width: 1.2
          }
        }
      } : {
        type: norm.xAxisType,
        data: norm.xData,
        axisLabel: {
          color: textColor,
          rotate: categoryCount > 12 ? 32 : 0,
          fontSize: categoryCount > 14 ? 11 : 12,
          fontWeight: 600,
          margin: 12,
          interval: categoryCount > 24 ? 2 : (categoryCount > 12 ? 1 : 0),
          hideOverlap: true
        },
        axisLine: {
          lineStyle: { color: borderColor, width: 1.5 }
        },
        axisTick: {
          lineStyle: { color: borderColor, width: 1.2 },
          length: 5
        }
      }
    ) : undefined,
    yAxis: !isPieChart ? (
      shouldUseHorizontalBar ? {
        type: 'category',
        data: norm.xData,
        inverse: true,
        axisLabel: {
          color: textColor,
          fontSize: 12,
          fontWeight: 600,
          width: 150,
          overflow: 'truncate',
          margin: 10
        },
        axisLine: { show: false },
        axisTick: { show: false }
      } : {
        type: 'value',
        axisLabel: {
          color: textColor,
          fontSize: 12,
          fontWeight: 600,
          margin: 12,
          formatter: (value) => formatMetricValue(value)
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          lineStyle: {
            color: borderColor,
            type: 'dashed',
            width: 1.2
          }
        }
      }
    ) : undefined,
    series: safeSeries.map((s, idx) => {
      if (s.type === 'pie') {
        // Para pie/doughnut, usar colores personalizados si existen
        const customColors = norm.customColors;
        const pieBorderColor = getComputedStyle(document.documentElement).getPropertyValue('--bg-primary').trim() || '#0A0A0A';
        return {
          ...s,
          center: ['60%', '50%'],
          color: Array.isArray(customColors) ? customColors.map(parseColor) : undefined,
          itemStyle: {
            borderRadius: 8,
            borderColor: pieBorderColor,
            borderWidth: 3
          },
          label: {
            color: textColor,
            fontSize: 13,
            fontWeight: 600,
            formatter: '{b}: {d}%'
          },
          emphasis: {
            focus: 'self',  // Resaltar solo este segmento
            itemStyle: {
              shadowBlur: 20,
              shadowColor: 'rgba(139, 92, 246, 0.6)'
            },
            label: {
              fontSize: 15,
              fontWeight: 700
            },
            scaleSize: 10  // Aumentar tamaño al hacer hover
          },
          blur: {
            itemStyle: {
              opacity: 0.3  // Atenuar segmentos no seleccionados
            }
          },
          select: {
            itemStyle: {
              borderWidth: 3,
              borderColor: '#8B5CF6'
            }
          },
          selectedMode: 'single',  // Permitir selección de segmentos
          animationDuration: 1200,
          animationEasing: 'cubicOut'
        };
      }
      
      // Para bar/line, usar colores del backend si existen
      const hasCustomColors = s.backgroundColor && Array.isArray(s.backgroundColor);
      const customColor = hasCustomColors ? parseColor(s.backgroundColor[0]) : null;
      const fallbackColors = colorPalette[idx % colorPalette.length];
      
      return {
        ...s,
        name: localizeChartLabel(s.name, isEnglishUi),
        // NO especificar barWidth individualmente, dejar que ECharts lo calcule con barCategoryGap y barGap
        itemStyle: hasCustomColors ? {
          // Si hay colores personalizados, usar un color sólido por barra
          color: (params) => {
            const colorStr = s.backgroundColor[params.dataIndex % s.backgroundColor.length];
            return parseColor(colorStr);
          },
          borderRadius: s.type === 'bar' ? [10, 10, 0, 0] : 0
        } : { 
          // Si no, usar gradiente por defecto
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: fallbackColors[0] },
              { offset: 1, color: fallbackColors[1] }
            ]
          },
          borderRadius: s.type === 'bar' ? [10, 10, 0, 0] : 0
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 15,
            shadowColor: customColor ? addOpacity(customColor, 0.6) : 'rgba(139, 92, 246, 0.6)'
          }
        },
        label: s.type === 'bar' ? {
          show: numSeries < 3 && categoryCount <= 10,
          position: shouldUseHorizontalBar ? 'right' : 'top',
          color: textColor,
          fontSize: 12,
          fontWeight: 700,
          formatter: (params) => formatMetricValue(params.value),
          distance: shouldUseHorizontalBar ? 8 : 6
        } : undefined,
        areaStyle: s.type === 'line' ? { 
          opacity: 0.3,
          color: customColor ? {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: addOpacity(customColor, 0.6) },
              { offset: 1, color: addOpacity(customColor, 0.1) }
            ]
          } : {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: addOpacity(fallbackColors[0], 0.6) },
              { offset: 1, color: addOpacity(fallbackColors[1], 0.1) }
            ]
          }
        } : undefined,
        smooth: s.type === 'line' ? 0.4 : undefined,
        lineStyle: s.type === 'line' ? {
          width: 4,
          color: customColor || fallbackColors[0]
        } : undefined,
        symbolSize: s.type === 'line' ? 10 : undefined,
        animationDuration: 1200,
        animationEasing: 'cubicOut'
      };
    })
    };
  }, [norm, colorPalette, hideTitle, chartMeta]);
  
  if (!option) return null;

  if (hideTitle) {
    return (
      <ReactECharts
        key={themeKey}
        ref={ref}
        option={option}
        style={{ height: chartHeight, width: '100%', minHeight: chartHeight, maxWidth: '100%' }}
        notMerge={true}
        lazyUpdate={true}
        opts={{ renderer: 'canvas' }}
      />
    );
  }

  // Colores para el JSX (header y botón)
  const textColor = getComputedStyle(document.documentElement).getPropertyValue('--text-primary').trim() || '#ECECEC';
  const borderColor = getComputedStyle(document.documentElement).getPropertyValue('--border-color').trim() || '#3a3a3a';

  const handleExport = () => {
    const inst = ref.current?.getEchartsInstance();
    if (!inst) return;
    
    // Obtener el color de fondo actual
    const bgColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--bg-tertiary').trim() || '#212121';
    
    const dataUrl = inst.getDataURL({ 
      type: 'png', 
      pixelRatio: 2, 
      backgroundColor: bgColor,
      excludeComponents: ['toolbox']
    });
    
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${norm.title.replace(/\s+/g, '_')}.png`;
    a.click();
  };

  return (
    <div className="chart-wrapper" style={{ 
      background: 'var(--bg-secondary)', 
      padding: '24px', 
      borderRadius: '12px', 
      border: '1px solid var(--border-color)',
      boxShadow: 'var(--shadow-sm)',
      animation: 'fadeIn 0.6s ease-out'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: `1px solid ${borderColor}`,
        gap: '16px'
      }}>
        <h3 style={{ 
          margin: 0, 
          fontSize: '18px', 
          fontWeight: 700,
          color: textColor,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flex: 1
        }}>
          {norm.title}
        </h3>
        <button 
          onClick={handleExport}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
            background: 'var(--bg-primary)',
            color: 'var(--text-secondary)',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: 'var(--shadow-sm)'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'var(--bg-hover)';
            e.target.style.color = 'var(--text-primary)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'var(--bg-primary)';
            e.target.style.color = 'var(--text-secondary)';
          }}
        >
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
            <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10M11.3333 5.33333L8 2M8 2L4.66667 5.33333M8 2V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Descargar PNG
        </button>
      </div>
      <ReactECharts 
        key={themeKey}
        ref={ref} 
        option={option} 
        style={{ height: 600, width: '100%', minHeight: 600, maxWidth: '100%' }} 
        notMerge={true} 
        lazyUpdate={true}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
}


